import { TrackSchemaWithUsage } from '../../../modules/page/checkout/model';
import Fragment from 'react-dot-fragment';
import React from 'react';
import { classes, style } from 'typestyle';
import { mediaDesktop, mediaTablet, mediaTabletOnly } from '../../../theme/media';
import { percent, px } from 'csx';
import { SVGIcon } from '../../svg-icon';
import { colorCrimson, colorDarkGreen } from '../../../theme/color';
import { VouchStatusDisplay } from './vouch-status';

export interface TrackInfoProps {
  track: TrackSchemaWithUsage;
}

const styles = {
  list: style(
    mediaTablet({
      display: 'flex',
      flexWrap: 'wrap',
    }),
  ),
  listItem: style(
    {
      listStyle: 'none',
    },
    mediaTabletOnly({
      display: 'inline-block',
      minWidth: percent(20),
    }),
    mediaDesktop({
      minWidth: percent(30),
    }),
  ),
  listItemLayout: style({
    display: 'flex',
    $nest: {
      '& > svg': {
        flexShrink: 0,
      },
    },
  }),
  listText: style({ marginLeft: px(10), marginRight: px(10) }),
  singleItemWrapper: style({ marginLeft: px(40) }),
};

const PLATFORM_NAMES = {
  youtube: 'YouTube',
  dailymotion: 'DailyMotion',
  linkedin: 'LinkedIn',
};

const toUpperFirst = (str: string) => str[0].toUpperCase() + str.substr(1);

const formatPlatform = (platformId: string) => PLATFORM_NAMES[platformId] || toUpperFirst(platformId);

export default class TrackInfo extends React.Component<TrackInfoProps> {
  render() {
    const { track } = this.props;

    const { video_platforms, rightsholders, restrictions, vouch_status } = track;

    const listedRightsholders = Array.isArray(rightsholders)
      ? rightsholders.filter((rightsholder) => rightsholder.is_listed)
      : [];

    return (
      <div>
        <h3>Allowed platforms</h3>
        <ul className={styles.list}>
          {video_platforms &&
            Object.entries(video_platforms).map(([name, allowed], index) => (
              <li key={index} className={classes(styles.listItem, styles.listItemLayout)}>
                {allowed ? (
                  <SVGIcon.Checkmark color={colorDarkGreen.toHexString()} size={16} />
                ) : (
                  <SVGIcon.Cross color={colorCrimson.toHexString()} size={16} />
                )}
                <span className={styles.listText}>{formatPlatform(name)}</span>
              </li>
            ))}
        </ul>
        {Array.isArray(restrictions) && (
          <span>
            <h3>Restrictions</h3>
            <ul className={styles.list}>
              {restrictions &&
                restrictions.map((restriction, index) => (
                  <li key={index} className={classes(styles.listItem, styles.listItemLayout)}>
                    <SVGIcon.Cross color={colorCrimson.toHexString()} size={16} />
                    <span className={styles.listText}>{toUpperFirst(restriction)}</span>
                  </li>
                ))}
            </ul>
          </span>
        )}
        <h3>Usage</h3>
        <ul className={styles.list}>
          <li className={classes(styles.listItem, styles.listItemLayout)}>
            <SVGIcon.Cross color={colorCrimson.toHexString()} size={16} />
            <span className={styles.listText}>No remixing</span>
          </li>
          <li className={classes(styles.listItem, styles.listItemLayout)}>
            <SVGIcon.Checkmark color={colorDarkGreen.toHexString()} size={16} />
            <span className={styles.listText}>1 licence per video</span>
          </li>
          <li className={styles.listItem}>
            <VouchStatusDisplay className={styles.listItemLayout} status={vouch_status} />
          </li>
        </ul>
        <h3>Branded channel</h3>
        <span className={classes(styles.singleItemWrapper, styles.listItemLayout)}>
          {track.branded_content ? (
            <SVGIcon.Checkmark color={colorDarkGreen.toHexString()} size={16} />
          ) : (
            <SVGIcon.Cross color={colorCrimson.toHexString()} size={16} />
          )}
          <span className={styles.listText}>
            {track.branded_content
              ? 'You may use this track for content on a branded channel.'
              : 'Sorry, you cannot use this track for content on a branded channel.'}
          </span>
        </span>
        <h3>Brand sponsored</h3>
        <span className={classes(styles.singleItemWrapper, styles.listItemLayout)}>
          {track.brand_sponsored ? (
            <SVGIcon.Checkmark color={colorDarkGreen.toHexString()} size={16} />
          ) : (
            <SVGIcon.Cross color={colorCrimson.toHexString()} size={16} />
          )}
          <span className={styles.listText}>
            {track.brand_sponsored
              ? 'You may use this track in a video that is paid promotion by a brand.'
              : 'Sorry, you cannot use this track in a video that is a paid promotion by a brand.'}
          </span>
        </span>
        {listedRightsholders.length > 0 && (
          <span>
            <h3>Rightsholders</h3>
            <ul className={styles.list}>
              {listedRightsholders.map((rightsholder, index) => (
                <li key={index} className={classes(styles.listItem, styles.listItemLayout)}>
                  <SVGIcon.Checkmark color={colorDarkGreen.toHexString()} size={16} />
                  <span className={styles.listText}>{rightsholder.name}</span>
                </li>
              ))}
            </ul>
          </span>
        )}
      </div>
    );
  }
}
